/**
 * 개요 페이지 위젯 배치 — 상단 바 4개 + 하단 그리드 위젯 9개(맵·특이사항 포함), 총 13블록.
 *
 * 고정된 key 집합이다 — 위젯을 새로 추가하거나 제거하는 UI는 없고, 이 13개의
 * 위치(x/y/w/h)만 admin/editor 가 자유롭게 바꿔서 저장한다 (전원 동일 노출).
 */
export const DASHBOARD_LAYOUT_KEYS = [
  'materials-summary',
  'contacts',
  'site-links',
  'tools',
  'operation-process',
  'schedule',
  'journal',
  'incidents',
  'ip-list',
  'photos',
  'materials-status',
  'map',
  'notes',
] as const;

export type DashboardLayoutKey = (typeof DASHBOARD_LAYOUT_KEYS)[number];

export interface DashboardLayoutItem {
  i: DashboardLayoutKey;
  x: number;
  y: number;
  w: number;
  h: number;
}

export type DashboardLayout = DashboardLayoutItem[];

export const DASHBOARD_GRID_COLUMNS = 12;
export const DASHBOARD_ROW_HEIGHT = 32;

/**
 * 오늘의 시각적 순서(상단 바 4개 전체폭 스택 → 3열 그리드)를 그대로 재현한
 * 기본 배치. 저장된 배치가 없는 프로젝트는 이 값으로 렌더링되어 지금과
 * 동일하게 보인다.
 */
export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = [
  { i: 'materials-summary', x: 0, y: 0, w: 12, h: 3 },
  { i: 'contacts', x: 0, y: 3, w: 12, h: 4 },
  { i: 'site-links', x: 0, y: 7, w: 12, h: 3 },
  { i: 'tools', x: 0, y: 10, w: 12, h: 3 },
  { i: 'operation-process', x: 0, y: 13, w: 4, h: 8 },
  { i: 'schedule', x: 4, y: 13, w: 4, h: 8 },
  { i: 'journal', x: 8, y: 13, w: 4, h: 8 },
  { i: 'incidents', x: 0, y: 21, w: 4, h: 8 },
  { i: 'ip-list', x: 4, y: 21, w: 4, h: 8 },
  { i: 'photos', x: 8, y: 21, w: 4, h: 8 },
  { i: 'materials-status', x: 0, y: 29, w: 4, h: 8 },
  // 맵을 알아보기 좋게 기본값은 넉넉히 잡되(h=14), MapOverviewWidget 은
  // MultiFloorCanvas 를 compact 모드로 써서 이보다 작게 줄여도 그대로 축소된다.
  { i: 'map', x: 4, y: 29, w: 4, h: 14 },
  { i: 'notes', x: 8, y: 29, w: 4, h: 14 },
];

/**
 * 저장된 배치를 고정 key 집합 기준으로 보정한다 — 알 수 없는 key 는 버리고,
 * 빠진 key 는 기본 배치 좌표로 채운다. 위젯 구성이 바뀌어도(예: 향후 블록 추가)
 * 과거에 저장된 배치가 깨지지 않게 하기 위함이다.
 */
export function normalizeDashboardLayout(saved: DashboardLayout | null | undefined): DashboardLayout {
  if (!saved || saved.length === 0) return DEFAULT_DASHBOARD_LAYOUT;

  const byKey = new Map(saved.filter((item) => DASHBOARD_LAYOUT_KEYS.includes(item.i)).map((item) => [item.i, item]));

  return DASHBOARD_LAYOUT_KEYS.map(
    (key) => byKey.get(key) ?? DEFAULT_DASHBOARD_LAYOUT.find((item) => item.i === key)!,
  );
}
